import React, { useState, CSSProperties, ReactElement, useEffect } from 'react';

import {
  useCSVReader,
  lightenDarkenColor,
  formatFileSize,
} from 'react-papaparse';
import { Entity } from '../types/entities/Entity';
import { Schema } from '../types/Schema';
import api from '../services/api';
import { SchemaWithClient } from '../types/SchemaWithClient';
import { toaster } from '../services/toaster';
import {
  EditorState,
  convertToRaw,
} from 'draft-js';
import { stateFromHTML } from 'draft-js-import-html';

const GREY = '#CCC';
const GREY_LIGHT = 'rgba(255, 255, 255, 0.4)';
const DEFAULT_REMOVE_HOVER_COLOR = '#A01919';
const REMOVE_HOVER_COLOR_LIGHT = lightenDarkenColor(
  DEFAULT_REMOVE_HOVER_COLOR,
  40
);
const GREY_DIM = '#686868';

const styles = {
  zone: {
    alignItems: 'center',
    border: `2px dashed ${GREY}`,
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    justifyContent: 'center',
    padding: 2,
    paddingLeft: 10,
    paddingRight: 10,
    color: '#58647d',
  } as CSSProperties,
  file: {
    display: 'flex',
    height: 'auto',
    width: 'auto',
    position: 'relative',
    zIndex: 10,
    flexDirection: 'row',
    justifyContent: 'center',
  } as CSSProperties,
  info: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 0,
    paddingRight: 0,
  } as CSSProperties,
  size: {
    backgroundColor: GREY_LIGHT,
    borderRadius: 3,
    marginBottom: '0',
    justifyContent: 'center',
    display: 'flex',
    marginRight: 10,
  } as CSSProperties,
  name: {
    backgroundColor: GREY_LIGHT,
    borderRadius: 3,
    marginBottom: '0',
  } as CSSProperties,
  progressBar: {
    bottom: 0,
    position: 'absolute',
    width: 'calc( 100% - 30px )',
    paddingLeft: 0,
    paddingRight: 0,
    height: 3,
    marginRight: 30,
    overflow: 'hidden',
    left: 0,
  } as CSSProperties,
  zoneHover: {
    borderColor: GREY_DIM,
  } as CSSProperties,
  default: {
    borderColor: GREY,
  } as CSSProperties,
  remove: {
    height: 23,
    width: 23,
    marginLeft: 10,
  } as CSSProperties,
};

type Props<T extends Entity> = {
  schema: Schema<T>;
  onSubmit: (item: Partial<T>) => void;
};

export const CSVReader = <T extends Entity>({
  schema,
  onSubmit: handleSubmit,
}: Props<T>): ReactElement => {
  const { CSVReader } = useCSVReader();
  const [zoneHover, setZoneHover] = useState(false);
  const [removeHoverColor, setRemoveHoverColor] = useState(
    DEFAULT_REMOVE_HOVER_COLOR
  );
  const [loader, setLoader] = React.useState(false);

  useEffect(() => {
    loader
      ? document.body.classList.add('loading')
      : document.body.classList.remove('loading');
  }, [loader]);

  const importHandle = async (results: any, database: string, schema: any) => {
    setLoader(true);

    await api
      .post(`/csv-import`, {
        data: results,
        database: database,
      })
      .then((response: any) => {
        if (response?.errors) {
          console.log(response?.errors);
          response?.errors.map((item: any) => {
            toaster.show({
              icon: 'error',
              message: `Error: ${item}`,
              intent: 'danger',
            });
          });
        }

        toaster.show({
          intent: 'success',
          message: `Successfully imported database`,
        });
      })
      .catch((error) => {
        toaster.show({
          icon: 'error',
          message: `Error: some rows were not imported`,
          intent: 'danger',
        });
      })
      .finally(() => {});
  };

  const convertHtmlToEditorState = (html: any) => {
    const contentState = stateFromHTML(html);
    return EditorState.createWithContent(contentState);
  };

  return (
    <CSVReader
      accept={'.csv, text/csv, .tsv, text/tsv'}
      onUploadAccepted={(results: any) => {
        console.log(results);
        let resultData: any = [];
        Object.keys(results.data).forEach(function (dataItem, index) {
          if (index) {
            let array: any = {};
            results.data[index].map((item: any, key: number) => {
              let keyName = results.data[0][key];

              if (item !== '') {
                if (keyName.indexOf('textarea') !== -1) {
                  const newEditorState = convertHtmlToEditorState(item);
                  const contentState = newEditorState.getCurrentContent();
                  const rawContentState = convertToRaw(contentState);
                  const jsonString = JSON.stringify(rawContentState);
                  item = jsonString;
                }

                if (keyName.indexOf('_multiswitchboard') !== -1) {
                  keyName = keyName.replace('_multiswitchboard', '');
                  let tempArray: any = [];
                  item.split(',').map((id: any) => {
                    tempArray.push({
                      id: id,
                    });
                  });
                  item = tempArray;
                }
                if (keyName.indexOf('_switchboard') !== -1) {
                  keyName = keyName.replace('_switchboard', '');
                  item = {
                    id: item,
                  };
                }
                if (keyName.indexOf('(') !== -1) {
                  keyName = keyName.replace(/\s\(.+\)/, '');
                }
                array[`${keyName}`] = item;
              }
            });
            resultData.push(array);
          }
        });

        let chunkSize = 200; // a darabok mérete
        let result = []; // a darabokat tartalmazó tömb
        for (let i = 0; i < resultData.length; i += chunkSize) {
          let chunk = resultData.slice(i, i + chunkSize); // kivágunk egy darabot a tömbből
          result.push(chunk); // hozzáadjuk a darabot a tömbhöz

          importHandle(
            chunk,
            window.location.href.split('/').slice(-1)[0],
            schema
          )
            .then(() => {})
            .finally(() => {
              setLoader(false);
              handleSubmit({});
            });
        }
        setZoneHover(false);
      }}
      onDragOver={(event: DragEvent) => {
        event.preventDefault();
        setZoneHover(true);
      }}
      onDragLeave={(event: DragEvent) => {
        event.preventDefault();
        setZoneHover(false);
      }}
    >
      {({
        getRootProps,
        acceptedFile,
        ProgressBar,
        getRemoveFileProps,
        Remove,
      }: any) => (
        <>
          <div
            {...getRootProps()}
            style={Object.assign(
              {},
              styles.zone,
              zoneHover && styles.zoneHover
            )}
          >
            {acceptedFile ? (
              <>
                <div style={styles.file}>
                  <div style={styles.info}>
                    <span style={styles.size}>
                      {formatFileSize(acceptedFile.size)}
                    </span>
                    <span style={styles.name}>{acceptedFile.name}</span>
                  </div>
                  <div style={styles.progressBar}>
                    <ProgressBar />
                  </div>
                  <div
                    {...getRemoveFileProps()}
                    style={styles.remove}
                    onMouseOver={(event: Event) => {
                      event.preventDefault();
                      setRemoveHoverColor(REMOVE_HOVER_COLOR_LIGHT);
                    }}
                    onMouseOut={(event: Event) => {
                      event.preventDefault();
                      setRemoveHoverColor(DEFAULT_REMOVE_HOVER_COLOR);
                    }}
                  >
                    <Remove color={removeHoverColor} />
                  </div>
                </div>
              </>
            ) : (
              'Drop CSV file here or click to upload'
            )}
          </div>
        </>
      )}
    </CSVReader>
  );
};

function schemaHasClient<T extends Entity>(
  schema: Schema<T>
): schema is SchemaWithClient<T> {
  return 'client' in schema;
}
